import { FormField, FormSection } from '../types/formBuilder';

/**
 * Create a new form field with default values
 */
export const createNewField = (): FormField => ({
  id: `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  type: '',
  label: '',
  variable_name: '',
  additional_variables: [],
  required: false,
  placeholder: '',
  description: '',
  advanced: false
});

/**
 * Create a new form section with default values
 */
export const createNewSection = (sectionCount: number): FormSection => ({
  id: `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  title: `Section ${sectionCount + 1}`,
  fields: [createNewField()]
});

/**
 * Get default placeholder text based on field type
 */
export const getDefaultPlaceholder = (fieldType: string, fieldLabel: string): string => {
  const labelLower = fieldLabel.toLowerCase() || 'value';
  
  switch (fieldType) {
    case 'text':
      return `Enter ${labelLower}`;
    case 'textarea':
      return `Enter ${labelLower}...`;
    case 'number':
      return '0';
    case 'email':
      return 'user@example.com';
    case 'password':
      return 'Enter password';
    case 'url':
      return 'https://example.com';
    case 'tel':
      return '+1 (555) 123-4567';
    case 'date':
      return 'YYYY-MM-DD';
    case 'time':
      return 'HH:MM';
    case 'color':
      return '#6b6b70';
    case 'file':
      return 'Click to upload or drag and drop';
    default:
      return `Enter ${labelLower}`;
  }
};

/**
 * Get default options for select fields
 */
export const getDefaultSelectOptions = () => [
  { label: 'Option 1', value: 'option1' },
  { label: 'Option 2', value: 'option2' }
];

/**
 * Validate form configuration
 */
export const validateFormConfig = (sections: FormSection[]): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (sections.length === 0) {
    errors.push('At least one section is required');
  }

  sections.forEach((section, sectionIndex) => {
    if (!section.title.trim()) {
      errors.push(`Section ${sectionIndex + 1} requires a title`);
    }

    if (section.fields.length === 0) {
      errors.push(`Section "${section.title}" must have at least one field`);
    }

    section.fields.forEach((field, fieldIndex) => {
      if (!field.type) {
        errors.push(`Field ${fieldIndex + 1} in section "${section.title}" requires a type`);
      }
      
      if (!field.label.trim()) {
        errors.push(`Field ${fieldIndex + 1} in section "${section.title}" requires a label`);
      }

      if (field.type === 'select' && (!field.options || field.options.length === 0)) {
        errors.push(`Select field "${field.label}" requires at least one option`);
      }

      if (field.type === 'range' && (field.min === undefined || field.max === undefined)) {
        errors.push(`Range field "${field.label}" requires min and max values`);
      }

      if (field.type === 'dropdown_with_image' && (!field.imageOptions || field.imageOptions.length === 0)) {
        errors.push(`Image dropdown field "${field.label}" requires at least one option with image`);
      }
    });
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Export form configuration to JSON
 */
export const exportFormConfig = (sections: FormSection[]) => {
  const config = {
    version: '1.0',
    created_at: new Date().toISOString(),
    sections: sections.map(section => ({
      title: section.title,
      fields: section.fields.map(field => ({
        type: field.type,
        label: field.label,
        variable_name: field.variable_name,
        required: field.required,
        placeholder: field.placeholder,
        description: field.description,
        advanced: field.advanced,
        defaultValue: field.defaultValue,
        min: field.min,
        max: field.max,
        step: field.step,
        rows: field.rows,
        options: field.options,
        multiple: field.multiple,
        accept: field.accept
      }))
    }))
  };

  return JSON.stringify(config, null, 2);
};